// Dice Factory v0.3 — Per-player sandbox / playtest version
// Pure TypeScript, no side effects. All randomness via seeded RNG (mulberry32).

export const SIDES_ORDER = [4, 6, 8, 10, 12] as const;
export type DieSidesV3 = 4 | 6 | 8 | 10 | 12;

export const DEFAULT_SLOT_LABELS: readonly string[] = [
  'Recruit',     // 1
  'Free Pips',   // 2
  'Promote',     // 3
  'Free Pips',   // 4
  'Bump Down',   // 5
  'Free Pips',   // 6
  'Mass Recruit',// 7
  'Free Pips',   // 8
  'Mass Promote',// 9
  'Free Pips',   // 10
  'Score',       // 11
  'Free Pips',   // 12
];

export interface DiceV3 {
  id: string;
  sides: DieSidesV3;
  value: number;
  greyed: boolean;
}

export interface SlotV3 {
  slotNumber: number; // 1–12
  label: string;
}

export interface PlayerV3 {
  id: string;
  name: string;
  dicePool: DiceV3[];
  slots: SlotV3[];
  vp: number;
  freePips: number;
}

export interface DFStateV3 {
  type: 'dice-factory';
  version: 'v0.3';
  phase: 'playing';
  players: Record<string, PlayerV3>;
  playerOrder: string[];
  rng: number;
}

// mulberry32 RNG — matches template convention, returns [nextSeed, floatValue]
function rngStep(seed: number): [number, number] {
  let s = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(s ^ (s >>> 15), 1 | s);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  const val = ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  return [s, val];
}

function rollDie(sides: number, rng: number): [number, number] {
  const [next, r] = rngStep(rng);
  return [Math.floor(r * sides) + 1, next];
}

function nextId(rng: number): [string, number] {
  const [next, r] = rngStep(rng);
  return [`d${Math.floor(r * 1e9)}`, next];
}

// ===== ACTION TYPES =====

export type DFActionV3 =
  | { type: 'v3_roll_all'; playerId: string }
  | { type: 'v3_reroll_die'; playerId: string; dieId: string }
  | { type: 'v3_add_die'; playerId: string; sides: DieSidesV3 }
  | { type: 'v3_remove_die'; playerId: string; dieId: string }
  | { type: 'v3_promote_die'; playerId: string; dieId: string }
  | { type: 'v3_demote_die'; playerId: string; dieId: string }
  | { type: 'v3_toggle_grey'; playerId: string; dieId: string }
  | { type: 'v3_update_slot'; playerId: string; slotNumber: number; label: string }
  | { type: 'v3_adjust_vp'; playerId: string; delta: number }
  | { type: 'v3_adjust_free_pips'; playerId: string; delta: number };

// ===== HELPERS =====

function defaultSlots(): SlotV3[] {
  return DEFAULT_SLOT_LABELS.map((label, i) => ({ slotNumber: i + 1, label }));
}

// ===== GAME CREATION =====

export function createGameV3(
  players: { id: string; name: string }[],
  seed: number,
): DFStateV3 {
  let rng = seed;
  const playerMap: Record<string, PlayerV3> = {};

  for (const p of players) {
    const dicePool: DiceV3[] = [];
    for (let i = 0; i < 3; i++) {
      let id: string, value: number;
      [id, rng] = nextId(rng);
      [value, rng] = rollDie(6, rng);
      dicePool.push({ id, sides: 6, value, greyed: false });
    }
    playerMap[p.id] = {
      id: p.id,
      name: p.name,
      dicePool,
      slots: defaultSlots(),
      vp: 0,
      freePips: 0,
    };
  }

  return {
    type: 'dice-factory',
    version: 'v0.3',
    phase: 'playing',
    players: playerMap,
    playerOrder: players.map(p => p.id),
    rng,
  };
}

// ===== ACTION APPLICATION =====

export function applyActionV3(state: DFStateV3, action: DFActionV3): DFStateV3 {
  let rng = state.rng;
  const players = { ...state.players };

  function updatePlayer(id: string, fn: (p: PlayerV3) => Partial<PlayerV3>): void {
    if (!players[id]) return;
    players[id] = { ...players[id], ...fn(players[id]) };
  }

  switch (action.type) {
    case 'v3_roll_all':
      updatePlayer(action.playerId, p => ({
        dicePool: p.dicePool.map(die => {
          if (die.greyed) return die;
          let value: number;
          [value, rng] = rollDie(die.sides, rng);
          return { ...die, value };
        }),
      }));
      break;

    case 'v3_reroll_die':
      updatePlayer(action.playerId, p => ({
        dicePool: p.dicePool.map(die => {
          if (die.id !== action.dieId || die.greyed) return die;
          let value: number;
          [value, rng] = rollDie(die.sides, rng);
          return { ...die, value };
        }),
      }));
      break;

    case 'v3_add_die': {
      let id: string, value: number;
      [id, rng] = nextId(rng);
      [value, rng] = rollDie(action.sides, rng);
      const newDie: DiceV3 = { id, sides: action.sides, value, greyed: false };
      updatePlayer(action.playerId, p => ({ dicePool: [...p.dicePool, newDie] }));
      break;
    }

    case 'v3_remove_die':
      updatePlayer(action.playerId, p => ({
        dicePool: p.dicePool.filter(d => d.id !== action.dieId),
      }));
      break;

    case 'v3_promote_die':
      updatePlayer(action.playerId, p => ({
        dicePool: p.dicePool.map(die => {
          if (die.id !== action.dieId) return die;
          const idx = SIDES_ORDER.indexOf(die.sides);
          const newSides = idx < SIDES_ORDER.length - 1 ? SIDES_ORDER[idx + 1] : die.sides;
          let value: number;
          [value, rng] = rollDie(newSides, rng);
          return { ...die, sides: newSides, value };
        }),
      }));
      break;

    case 'v3_demote_die':
      updatePlayer(action.playerId, p => ({
        dicePool: p.dicePool.map(die => {
          if (die.id !== action.dieId) return die;
          const idx = SIDES_ORDER.indexOf(die.sides);
          const newSides = idx > 0 ? SIDES_ORDER[idx - 1] : die.sides;
          let value: number;
          [value, rng] = rollDie(newSides, rng);
          return { ...die, sides: newSides, value };
        }),
      }));
      break;

    case 'v3_toggle_grey':
      updatePlayer(action.playerId, p => ({
        dicePool: p.dicePool.map(die =>
          die.id === action.dieId ? { ...die, greyed: !die.greyed } : die
        ),
      }));
      break;

    case 'v3_update_slot':
      updatePlayer(action.playerId, p => ({
        slots: p.slots.map(s =>
          s.slotNumber === action.slotNumber ? { ...s, label: action.label } : s
        ),
      }));
      break;

    case 'v3_adjust_vp':
      updatePlayer(action.playerId, p => ({ vp: Math.max(0, p.vp + action.delta) }));
      break;

    case 'v3_adjust_free_pips':
      updatePlayer(action.playerId, p => ({ freePips: Math.max(0, p.freePips + action.delta) }));
      break;
  }

  return { ...state, players, rng };
}

// v0.3 has no hidden information — full state is the player view
export function getPlayerViewV3(state: DFStateV3): DFStateV3 {
  return state;
}

export function describeV3(): string {
  return `Dice Factory v0.3 — Sandbox playtest.
Each player manages their own dice pool (d4/d6/d8/d10/d12).
Actions: roll all, reroll specific dice, add/remove dice, promote/demote dice, grey out dice.
12 ability slots per player with editable labels.
VP and Free Pip counters per player.`;
}
